// 右上コントロール（参加者一覧・共有タイマー）の実測幅を CSS 変数
// `--canvas-top-right-width` へ反映する。
//
// 左上のアプリメニューは、右上コントロールの下に潜って押せなくなるのを避けるため、
// 折り返し幅の上限からこの幅を差し引いている。差し引き量を「最大構成の見積もり」で
// 固定すると、参加者が少なく実際の右上コントロールが狭い場合でも同じだけ引かれてしまい、
// 画面幅に余裕があるのにアプリメニューが折り返してしまう。実測値を渡すことで、
// 折り返しを「本当に幅が足りないとき」だけに限定する。
//
// 参加者の増減やタイマー表示の変化で幅が変わるため、ResizeObserver で追従する。

import { useEffect, type RefObject } from "react";

/** アプリメニューの折り返し幅の上限から差し引く幅を保持する CSS 変数名。 */
const RESERVE_VARIABLE = "--canvas-top-right-width";

export function useTopRightReserve(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    // ResizeObserver 非対応環境では実測値を書き込まず、CSS 側の既定値
    // （最大構成の見積もり 18rem）へ委ねる。初回の実測値だけを固定してしまうと、
    // その後に参加者が増えて右上コントロールが広がったときに追従できず、
    // アプリメニューが再びその下へ潜って押せなくなるため。
    if (typeof ResizeObserver === "undefined") return undefined;

    const root = document.documentElement;
    const applyWidth = (): void => {
      // 小数幅で 1px 足りずに折り返すことがないよう、切り上げた整数 px を渡す。
      const width = Math.ceil(element.getBoundingClientRect().width);
      root.style.setProperty(RESERVE_VARIABLE, `${width}px`);
    };

    applyWidth();

    const observer = new ResizeObserver(applyWidth);
    observer.observe(element);
    return () => {
      observer.disconnect();
      // アンマウント後は CSS 側の既定値（最大構成の見積もり）へ戻す。
      root.style.removeProperty(RESERVE_VARIABLE);
    };
  }, [ref]);
}
